import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Building2,
  UserCheck,
  AlertTriangle,
  Clock,
  ShieldCheck,
  Activity as ActivityIcon,
} from "lucide-react";
import {
  fetchRecentScans,
  fetchScanDaySummary,
  type RecentScan,
  type ScanDaySummary,
} from "../../services/scans";
import { fetchHospitals, type Hospital } from "../../services/hospitals";
import { buildHospitalColorMap } from "../../utils/hospitalColors";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { fetchOfficers, type Officer } from "../../services/officers";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";

// Gold accent used to mark "today" throughout the brand UI.
const GOLD = "#DDAF41";
const BRAND_BLUE = "#3B7BBE";

type ViewMode = "month" | "week" | "day" | "list";

// ── Date helpers (no external date lib in this project) ─────────────────────
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
// Sunday-anchored start of week.
function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  return addDays(x, -x.getDay());
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
/** Local calendar date as YYYY-MM-DD (the summary API's day format). */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// The raw scan list endpoint returns at most this many rows (newest first).
const RAW_ROW_CAP = 50000;

/** A scan counts as a violation/alert when it isn't a clean in-range scan. */
function isViolation(s: RecentScan): boolean {
  return s.status !== "OK" || s.proximity_status === "OUT_OF_RANGE";
}

interface DayBucket {
  date: Date;
  /** Raw scans — only loaded in Day/List views. Empty in Month/Week (summary). */
  scans: RecentScan[];
  /** Scans per hour of day (0–23). */
  hourly: number[];
  count: number;
  alerts: number;
  hospitals: string[]; // hospital names active that day, most-active first
}

export function CalendarPage() {
  const { t, lang } = useTranslation();

  const [view, setView] = useState<ViewMode>("month");
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  // Shared, persisted hospital selection (carries across pages). The native
  // <select> below expects a string ("" = all), so adapt the hook's
  // string | undefined value with `?? ""`.
  const [stickyHospitalId, setHospitalId] = useStickyHospital();
  const hospitalId = stickyHospitalId ?? "";
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  // Officer filter: "" = everyone, "__unattributed" = scans with no officer,
  // otherwise an officer id. Month/Week send it to the server with the summary
  // request; Day/List filter the loaded rows client-side.
  const [officerId, setOfficerId] = useState<string>("");
  const [officerRoster, setOfficerRoster] = useState<Officer[]>([]);
  const [scans, setScans] = useState<RecentScan[]>([]);
  // Per-day totals counted in the database (Month/Week). null in Day/List.
  const [summary, setSummary] = useState<ScanDaySummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  // Month and Week render from server-side per-day totals, which have no row
  // cap. They used to download every raw scan in the window and count in the
  // browser; a month is >50,000 scans, so the API's row limit silently dropped
  // the oldest days (e.g. Sep 1–2 showed as empty). Day and List show individual
  // scans, so they load raw rows, for a window small enough to stay under the cap.
  const usesSummary = view === "month" || view === "week";

  // The data range we need to load for the current view. Month widens to the
  // visible 6-week grid; List shows one week of individual scans at a time.
  const range = useMemo(() => {
    if (view === "week" || view === "list") {
      const start = startOfWeek(anchor);
      return { start, end: endOfDay(addDays(start, 6)) };
    }
    if (view === "day") {
      return { start: startOfDay(anchor), end: endOfDay(anchor) };
    }
    // month
    const gridStart = startOfWeek(startOfMonth(anchor));
    const gridEnd = endOfDay(addDays(gridStart, 41));
    return { start: gridStart, end: gridEnd };
  }, [view, anchor]);

  // Load hospital list once for the filter + stable color coding.
  useEffect(() => {
    fetchHospitals()
      .then(setHospitals)
      .catch(() => setHospitals([]));
    fetchOfficers({ status: "ALL" })
      .then(setOfficerRoster)
      .catch(() => setOfficerRoster([]));
  }, []);

  // Scans narrowed by the officer filter; every view below renders from this.
  const visibleScans = useMemo(() => {
    if (!officerId) return scans;
    if (officerId === "__unattributed") return scans.filter((s) => !s.officer_id);
    return scans.filter((s) => s.officer_id === officerId);
  }, [scans, officerId]);

  // Summary requests are filtered by officer on the server, so the officer is
  // part of the reload key there; raw rows are filtered client-side.
  const rangeKey = `${usesSummary ? "sum" : "raw"}-${range.start.getTime()}-${range.end.getTime()}-${hospitalId}-${usesSummary ? officerId : ""}`;

  const reload = useMemo(
    () => async () => {
      setLoading(true);
      setError(null);
      try {
        if (usesSummary) {
          const days = await fetchScanDaySummary({
            startDay: ymd(range.start),
            endDay: ymd(range.end),
            hospitalId: hospitalId || undefined,
            officerId: officerId || undefined,
          });
          setSummary(days);
          setScans([]);
        } else {
          const data = await fetchRecentScans(hospitalId || undefined, "today", {
            startDate: range.start.toISOString(),
            endDate: range.end.toISOString(),
          });
          setScans(data);
          setSummary(null);
        }
      } catch (e: any) {
        setError(e?.message || t("calendar.loadFailed", "Failed to load activity"));
        setScans([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey]
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  // Canonical hospital → color map, shared with the Scan Map and the comparison
  // chart (utils/hospitalColors), built from the full hospital list so a hospital
  // reads with the same color on every surface.
  const hospitalColors = useMemo(() => buildHospitalColorMap(hospitals), [hospitals]);

  const hospitalColor = (name: string | undefined) =>
    hospitalColors.colorFor({ name: name || t("calendar.unknownHospital", "Unknown hospital") });

  // Per-day buckets for every view. Month/Week build them from the server's
  // totals (already officer-filtered); Day/List build them from visibleScans.
  const bucketByDay = useMemo(() => {
    const map = new Map<string, DayBucket>();
    if (summary) {
      const unknown = t("calendar.unknownHospital", "Unknown hospital");
      for (const d of summary) {
        const [y, m, dd] = d.day.split("-").map(Number);
        const date = new Date(y, m - 1, dd);
        map.set(dayKey(date), {
          date,
          scans: [],
          hourly: Array.isArray(d.hourly) && d.hourly.length === 24 ? d.hourly : new Array(24).fill(0),
          count: d.count,
          alerts: d.alerts,
          hospitals: d.hospitals.map((h) => h.name || unknown),
        });
      }
      return map;
    }
    for (const s of visibleScans) {
      const d = new Date(s.timestamp);
      if (Number.isNaN(d.getTime())) continue;
      const key = dayKey(d);
      let bucket = map.get(key);
      if (!bucket) {
        bucket = { date: startOfDay(d), scans: [], hourly: new Array(24).fill(0), count: 0, alerts: 0, hospitals: [] };
        map.set(key, bucket);
      }
      bucket.scans.push(s);
      bucket.hourly[d.getHours()] += 1;
      bucket.count += 1;
      if (isViolation(s)) bucket.alerts += 1;
    }
    // Resolve the hospital ordering (most active first) for each day.
    for (const bucket of map.values()) {
      const counts = new Map<string, number>();
      for (const s of bucket.scans) {
        const name = s.hospital_name || t("calendar.unknownHospital", "Unknown hospital");
        counts.set(name, (counts.get(name) || 0) + 1);
      }
      bucket.hospitals = Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
      bucket.scans.sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScans, summary]);

  // Raw rows hit the API cap: the oldest scans in the window were dropped.
  const truncated = !usesSummary && scans.length >= RAW_ROW_CAP;

  // Header stats. Month view loads the whole 6-week grid (so the leading and
  // trailing days of the neighbouring months can be shown), but its totals
  // must cover only the month being viewed. Week / Day / List count exactly
  // what is on screen.
  const stats = useMemo(() => {
    let total = 0;
    let alerts = 0;
    const activeDays = new Set<string>();
    const month = anchor.getMonth();
    const year = anchor.getFullYear();
    for (const b of bucketByDay.values()) {
      if (view === "month" && (b.date.getMonth() !== month || b.date.getFullYear() !== year)) continue;
      total += b.count;
      alerts += b.alerts;
      if (b.count > 0) activeDays.add(dayKey(b.date));
    }
    return { total, alerts, activeDays: activeDays.size };
  }, [bucketByDay, view, anchor]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = (dir: -1 | 1) => {
    setAnchor((prev) => {
      if (view === "week" || view === "list") return startOfDay(addDays(prev, dir * 7));
      if (view === "day") return startOfDay(addDays(prev, dir));
      return startOfMonth(addMonths(prev, dir)); // month
    });
  };
  const goToday = () => {
    setAnchor(startOfDay(new Date()));
  };

  // Open a specific day in Day view (drill-down from any other view).
  const drillToDay = (d: Date) => {
    setAnchor(startOfDay(d));
    setView("day");
  };

  // Localized header label for the current focus.
  const headerLabel = useMemo(() => {
    if (view === "day") {
      return new Intl.DateTimeFormat(lang, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(anchor);
    }
    if (view === "week" || view === "list") {
      const ws = startOfWeek(anchor);
      const we = addDays(ws, 6);
      const fmt = new Intl.DateTimeFormat(lang, { month: "short", day: "numeric" });
      return `${fmt.format(ws)} – ${fmt.format(we)}, ${we.getFullYear()}`;
    }
    return new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(anchor);
  }, [view, anchor, lang]);

  // ── Shared theming tokens (Tailwind class strings, dark-mode aware) ─────────
  const card =
    "rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-[#33334d] dark:bg-[#24243e]";

  const views: { key: ViewMode; labelKey: string; fallback: string }[] = [
    { key: "month", labelKey: "calendar.viewMonth", fallback: "Month" },
    { key: "week", labelKey: "calendar.viewWeek", fallback: "Week" },
    { key: "day", labelKey: "calendar.viewDay", fallback: "Day" },
    { key: "list", labelKey: "calendar.viewList", fallback: "List" },
  ];

  return (
    <div className="space-y-4">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
            <CalendarDays className="h-6 w-6" style={{ color: BRAND_BLUE }} />
            {t("calendar.title", "Patrol Activity Calendar")}
          </h1>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t("calendar.subtitle", "Scan activity across every checkpoint, day by day")}
          </p>
        </div>

        {/* Summary stats */}
        <div className="flex flex-wrap items-center gap-2">
          <StatPill
            icon={<ActivityIcon className="h-4 w-4" />}
            value={t("calendar.totalScans", "{n} scans").replace("{n}", stats.total.toLocaleString(lang))}
            tone="blue"
          />
          <StatPill
            icon={<ShieldCheck className="h-4 w-4" />}
            value={t("calendar.activeDays", "{n} active days").replace("{n}", stats.activeDays.toLocaleString(lang))}
            tone="green"
          />
          <StatPill
            icon={<AlertTriangle className="h-4 w-4" />}
            value={t("calendar.alertsCount", "{n} alerts").replace("{n}", stats.alerts.toLocaleString(lang))}
            tone={stats.alerts > 0 ? "red" : "muted"}
          />
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className={cn(card, "flex flex-wrap items-center gap-3 p-3")}>
        {/* Prev / label / next */}
        <div className="flex items-center gap-1">
          <IconButton onClick={() => navigate(-1)} ariaLabel="Previous">
            <ChevronLeft className="h-5 w-5" />
          </IconButton>
          <div className="min-w-[12rem] px-2 text-center text-base font-bold capitalize text-gray-900 dark:text-gray-100">
            {headerLabel}
          </div>
          <IconButton onClick={() => navigate(1)} ariaLabel="Next">
            <ChevronRight className="h-5 w-5" />
          </IconButton>
        </div>

        <button
          onClick={goToday}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-[#33334d] dark:text-gray-200 dark:hover:bg-[#33334d]"
        >
          {t("calendar.today", "Today")}
        </button>

        {/* View toggle */}
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-[#33334d] dark:bg-[#222]">
          {views.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-bold transition-all",
                view === v.key
                  ? "bg-white text-[#3B7BBE] shadow-sm dark:bg-[#33334d] dark:text-white"
                  : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              )}
            >
              {t(v.labelKey, v.fallback)}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Hospital filter */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-2 py-1.5 dark:border-[#33334d]">
            <Building2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <select
              value={hospitalId}
              onChange={(e) => setHospitalId(e.target.value)}
              className="appearance-none bg-transparent pr-1 text-xs font-bold text-gray-800 focus:outline-none dark:text-gray-100"
            >
              <option value="">{t("calendar.allHospitals", "All hospitals")}</option>
              {hospitals.map((h) => (
                <option key={h.id} value={h.id} className="text-gray-900">
                  {h.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 dark:border-[#33334d]">
            <UserCheck className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <select
              value={officerId}
              onChange={(e) => setOfficerId(e.target.value)}
              className="appearance-none bg-transparent pr-1 text-xs font-bold text-gray-800 focus:outline-none dark:text-gray-100"
            >
              <option value="">{t("calendar.allOfficers", "All officers")}</option>
              <option value="__unattributed">{t("calendar.unattributed", "Unattributed scans")}</option>
              {officerRoster.map((o) => (
                <option key={o.id} value={o.id} className="text-gray-900">
                  {o.name} ({o.n_number})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => void reload()}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-[#33334d] dark:text-gray-200 dark:hover:bg-[#33334d]"
            title={t("calendar.refresh", "Refresh")}
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            {t("calendar.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {truncated && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
          {t(
            "calendar.truncated",
            "Showing the most recent {n} scans in this window; earlier scans are not listed. Open a single day to see all of its scans."
          ).replace("{n}", RAW_ROW_CAP.toLocaleString(lang))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {/* ── View body (animated on view / range change) ─────────────────── */}
      <div className={cn(card, "relative overflow-hidden p-3 md:p-4")}>
        <div
          key={`${view}-${rangeKey}`}
          className="animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          {view === "month" && (
            <MonthView
              anchor={anchor}
              today={today}
              bucketByDay={bucketByDay}
              hospitalColor={hospitalColor}
              onSelectDay={drillToDay}
            />
          )}
          {view === "week" && (
            <WeekView
              anchor={anchor}
              today={today}
              bucketByDay={bucketByDay}
              onSelectDay={drillToDay}
            />
          )}
          {view === "day" && (
            <DayView
              anchor={anchor}
              bucket={bucketByDay.get(dayKey(anchor)) || null}
              hospitalColor={hospitalColor}
            />
          )}
          {view === "list" && (
            <ListView
              scans={visibleScans}
              hospitalColor={hospitalColor}
              onSelectDay={drillToDay}
            />
          )}
        </div>

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm dark:bg-[#24243e]/50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#3B7BBE] border-t-transparent" />
              <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                {t("calendar.loading", "Loading activity…")}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat pill ────────────────────────────────────────────────────────────────
function StatPill({
  icon,
  value,
  tone,
}: {
  icon: React.ReactNode;
  value: string;
  tone: "blue" | "green" | "red" | "muted";
}) {
  const tones: Record<string, string> = {
    blue: "bg-[#3B7BBE]/10 text-[#3B7BBE] dark:bg-[#3B7BBE]/20",
    green: "bg-[#A1BF8C]/20 text-[#4F8B3D] dark:text-[#A1BF8C]",
    red: "bg-[#EE3E2E]/10 text-[#EE3E2E]",
    muted: "bg-gray-100 text-gray-500 dark:bg-[#33334d] dark:text-gray-400",
  };
  return (
    <span
      className={cn(
        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-bold",
        tones[tone]
      )}
    >
      {icon}
      {value}
    </span>
  );
}

function IconButton({
  children,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#33334d]"
    >
      {children}
    </button>
  );
}

// Localized weekday short names (Sun..Sat), driven by Intl so they follow locale.
function useWeekdayLabels(lang: string, format: "short" | "narrow" = "short"): string[] {
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: format });
    // 2023-01-01 was a Sunday — anchor the week there.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)));
  }, [lang, format]);
}

// Heat background for a day cell, scaled against the busiest day in view.
function heatStyle(count: number, max: number): React.CSSProperties {
  if (count <= 0) return {};
  const ratio = max > 0 ? count / max : 0;
  const alpha = 0.14 + ratio * 0.72; // 0.14 → 0.86
  return { backgroundColor: `rgba(59, 123, 190, ${alpha.toFixed(3)})` };
}

// ── Month view ───────────────────────────────────────────────────────────────
function MonthView({
  anchor,
  today,
  bucketByDay,
  hospitalColor,
  onSelectDay,
}: {
  anchor: Date;
  today: Date;
  bucketByDay: Map<string, DayBucket>;
  hospitalColor: (name: string | undefined) => string;
  onSelectDay: (d: Date) => void;
}) {
  const { t, lang } = useTranslation();
  const weekdays = useWeekdayLabels(lang, "short");

  const gridStart = startOfWeek(startOfMonth(anchor));
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const currentMonth = anchor.getMonth();

  const maxCount = useMemo(() => {
    let m = 0;
    for (const d of days) {
      const b = bucketByDay.get(dayKey(d));
      if (b && b.count > m) m = b.count;
    }
    return m;
  }, [days, bucketByDay]);

  return (
    <div>
      {/* Weekday header */}
      <div className="mb-1 grid grid-cols-7 gap-1.5">
        {weekdays.map((w) => (
          <div
            key={w}
            className="px-1 py-1 text-center text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d) => {
          const b = bucketByDay.get(dayKey(d));
          const inMonth = d.getMonth() === currentMonth;
          const isToday = isSameDay(d, today);
          const count = b?.count ?? 0;

          return (
            <button
              key={dayKey(d)}
              onClick={() => onSelectDay(d)}
              className={cn(
                "group relative flex min-h-[84px] flex-col rounded-xl border p-1.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md md:min-h-[104px]",
                inMonth
                  ? "border-gray-200 dark:border-[#3d3d3d]"
                  : "border-transparent opacity-45",
                isToday && "ring-2 ring-offset-1 dark:ring-offset-[#24243e]"
              )}
              style={{
                ...heatStyle(count, maxCount),
                ...(isToday ? { ["--tw-ring-color" as any]: GOLD } : {}),
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                    isToday
                      ? "text-white"
                      : count > 0
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400"
                  )}
                  style={isToday ? { backgroundColor: GOLD } : undefined}
                >
                  {d.getDate()}
                </span>
                {b && b.alerts > 0 && (
                  <span
                    className="flex items-center gap-0.5 rounded-md bg-[#EE3E2E] px-1.5 py-0.5 text-[10px] font-bold text-white"
                    title={t("calendar.violationsCount", "{n} violations").replace("{n}", String(b.alerts))}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {b.alerts}
                  </span>
                )}
              </div>

              {count > 0 && (
                <>
                  <div className="mt-auto text-sm font-extrabold tabular-nums text-gray-900 dark:text-white">
                    {count}
                  </div>
                  {/* Hospital color dots */}
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {b!.hospitals.slice(0, 5).map((name) => (
                      <span
                        key={name}
                        className="h-2 w-2 rounded-full ring-1 ring-white/70 dark:ring-black/30"
                        style={{ backgroundColor: hospitalColor(name) }}
                        title={name}
                      />
                    ))}
                    {b!.hospitals.length > 5 && (
                      <span
                        className="inline-flex items-center rounded-md bg-white/95 px-1.5 py-0.5 text-[10px] font-bold leading-none text-gray-700 shadow-sm ring-1 ring-black/10 dark:bg-[#1f1f1f]/90 dark:text-gray-100 dark:ring-white/20"
                        title={b!.hospitals.slice(5).join(", ")}
                      >
                        +{b!.hospitals.length - 5}
                      </span>
                    )}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Heat legend */}
      <div className="mt-3 flex items-center justify-end gap-2 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
        <span>{t("calendar.legendLess", "Less")}</span>
        {[0.14, 0.32, 0.5, 0.68, 0.86].map((a) => (
          <span
            key={a}
            className="h-3 w-5 rounded"
            style={{ backgroundColor: `rgba(59, 123, 190, ${a})` }}
          />
        ))}
        <span>{t("calendar.legendMore", "More")}</span>
      </div>
    </div>
  );
}

// ── Week view ────────────────────────────────────────────────────────────────
function WeekView({
  anchor,
  today,
  bucketByDay,
  onSelectDay,
}: {
  anchor: Date;
  today: Date;
  bucketByDay: Map<string, DayBucket>;
  onSelectDay: (d: Date) => void;
}) {
  const { t, lang } = useTranslation();
  const ws = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const weekdayFmt = new Intl.DateTimeFormat(lang, { weekday: "short" });

  // counts[dayIndex][hour]
  const { counts, maxCell, dayTotals } = useMemo(() => {
    const counts: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
    const dayTotals = new Array(7).fill(0);
    let maxCell = 0;
    days.forEach((d, di) => {
      const b = bucketByDay.get(dayKey(d));
      if (!b) return;
      for (let h = 0; h < 24; h++) {
        const c = b.hourly[h] || 0;
        counts[di][h] += c;
        dayTotals[di] += c;
        if (counts[di][h] > maxCell) maxCell = counts[di][h];
      }
    });
    return { counts, maxCell, dayTotals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, bucketByDay]);

  const hours = Array.from({ length: 24 }, (_, h) => h);
  const hourLabel = (h: number) =>
    new Intl.DateTimeFormat(lang, { hour: "numeric" }).format(new Date(2023, 0, 1, h));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[680px]">
        {/* Day header row */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)] gap-1">
          <div />
          {days.map((d) => {
            const isToday = isSameDay(d, today);
            const di = d.getDay();
            return (
              <button
                key={dayKey(d)}
                onClick={() => onSelectDay(d)}
                className={cn(
                  "flex flex-col items-center rounded-lg px-1 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-[#33334d]",
                  isToday && "bg-[#DDAF41]/10"
                )}
              >
                <span className="text-[11px] font-bold uppercase text-gray-400 dark:text-gray-500">
                  {weekdayFmt.format(d)}
                </span>
                <span
                  className={cn(
                    "mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold",
                    isToday ? "text-white" : "text-gray-800 dark:text-gray-100"
                  )}
                  style={isToday ? { backgroundColor: GOLD } : undefined}
                >
                  {d.getDate()}
                </span>
                <span className="mt-0.5 text-[10px] font-semibold text-[#3B7BBE]">
                  {t("calendar.scansShort", "{n} scans").replace("{n}", String(dayTotals[di]))}
                </span>
              </button>
            );
          })}
        </div>

        {/* Hourly grid */}
        <div className="mt-1 max-h-[58vh] overflow-y-auto pr-1">
          {hours.map((h) => (
            <div key={h} className="grid grid-cols-[56px_repeat(7,1fr)] gap-1">
              <div className="flex h-7 items-center justify-end pr-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500">
                {hourLabel(h)}
              </div>
              {days.map((d, di) => {
                const c = counts[di][h];
                const isToday = isSameDay(d, today);
                return (
                  <button
                    key={`${dayKey(d)}-${h}`}
                    onClick={() => onSelectDay(d)}
                    title={
                      c > 0
                        ? `${hourLabel(h)} · ${t("calendar.scansShort", "{n} scans").replace("{n}", String(c))}`
                        : undefined
                    }
                    className={cn(
                      "h-7 rounded-md border transition-all hover:ring-1 hover:ring-[#3B7BBE]",
                      isToday ? "border-[#DDAF41]/40" : "border-gray-100 dark:border-[#363636]"
                    )}
                    style={heatStyle(c, maxCell)}
                  >
                    {c > 0 && (
                      <span
                        className={cn(
                          "text-[10px] font-bold tabular-nums",
                          c / Math.max(maxCell, 1) > 0.55
                            ? "text-white"
                            : "text-gray-700 dark:text-gray-200"
                        )}
                      >
                        {c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Day view ─────────────────────────────────────────────────────────────────
function DayView({
  anchor,
  bucket,
  hospitalColor,
}: {
  anchor: Date;
  bucket: DayBucket | null;
  hospitalColor: (name: string | undefined) => string;
}) {
  const { t, lang } = useTranslation();
  const scans = bucket?.scans ?? [];

  // Hourly distribution for the mini bar chart.
  const hourly = useMemo(() => {
    const arr = new Array(24).fill(0);
    for (const s of scans) arr[new Date(s.timestamp).getHours()] += 1;
    return arr;
  }, [scans]);
  const maxHour = Math.max(1, ...hourly);

  if (scans.length === 0) {
    return <EmptyState />;
  }

  // Sort ascending by time for a readable timeline, grouped by hour.
  const ordered = [...scans].sort((a, b) => +new Date(a.timestamp) - +new Date(b.timestamp));
  let lastHour = -1;
  const hourFmt = new Intl.DateTimeFormat(lang, { hour: "numeric" });

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex flex-wrap items-center gap-3 text-sm font-semibold text-gray-600 dark:text-gray-300">
        <span className="flex items-center gap-1.5">
          <ActivityIcon className="h-4 w-4 text-[#3B7BBE]" />
          {t("calendar.totalScans", "{n} scans").replace("{n}", scans.length.toLocaleString(lang))}
        </span>
        <span className="flex items-center gap-1.5">
          <Building2 className="h-4 w-4 text-gray-400" />
          {t("calendar.hospitalsActive", "{n} hospitals active").replace(
            "{n}",
            String(bucket?.hospitals.length ?? 0)
          )}
        </span>
        {bucket && bucket.alerts > 0 && (
          <span className="flex items-center gap-1.5 text-[#EE3E2E]">
            <AlertTriangle className="h-4 w-4" />
            {t("calendar.violationsCount", "{n} violations").replace("{n}", String(bucket.alerts))}
          </span>
        )}
      </div>

      {/* Hourly distribution */}
      <div>
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {t("calendar.hourlyDistribution", "Hourly distribution")}
        </div>
        <div className="flex h-24 items-end gap-0.5">
          {hourly.map((c, h) => (
            <div key={h} className="flex flex-1 flex-col items-center justify-end gap-1" title={`${hourFmt.format(new Date(2023, 0, 1, h))} · ${c}`}>
              <div
                className="w-full rounded-t bg-[#3B7BBE] transition-all"
                style={{ height: `${(c / maxHour) * 100}%`, opacity: c > 0 ? 1 : 0.12, minHeight: c > 0 ? 3 : 2 }}
              />
              {h % 6 === 0 && (
                <span className="text-[8px] font-semibold text-gray-400">{h}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Event timeline */}
      <div>
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {t("calendar.dayEvents", "Scan events")}
        </div>
        <div className="max-h-[44vh] space-y-1.5 overflow-y-auto pr-1">
          {ordered.map((s) => {
            const d = new Date(s.timestamp);
            const hr = d.getHours();
            const showHour = hr !== lastHour;
            lastHour = hr;
            return (
              <div key={s.id}>
                {showHour && (
                  <div className="mb-1 mt-2 flex items-center gap-2 first:mt-0">
                    <Clock className="h-3 w-3 text-gray-400" />
                    <span className="text-[11px] font-bold uppercase text-gray-400 dark:text-gray-500">
                      {hourFmt.format(d)}
                    </span>
                    <span className="h-px flex-1 bg-gray-100 dark:bg-[#3a3a3a]" />
                  </div>
                )}
                <ScanRow scan={s} hospitalColor={hospitalColor} showDate={false} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── List view ────────────────────────────────────────────────────────────────
function ListView({
  scans,
  hospitalColor,
  onSelectDay,
}: {
  scans: RecentScan[];
  hospitalColor: (name: string | undefined) => string;
  onSelectDay: (d: Date) => void;
}) {
  const { lang } = useTranslation();

  // Newest first, grouped by calendar day with sticky day headers.
  const groups = useMemo(() => {
    const ordered = [...scans].sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp));
    const map = new Map<string, { date: Date; scans: RecentScan[] }>();
    for (const s of ordered) {
      const d = new Date(s.timestamp);
      if (Number.isNaN(d.getTime())) continue;
      const key = dayKey(d);
      let g = map.get(key);
      if (!g) {
        g = { date: startOfDay(d), scans: [] };
        map.set(key, g);
      }
      g.scans.push(s);
    }
    return Array.from(map.values());
  }, [scans]);

  if (groups.length === 0) return <EmptyState />;

  const dayFmt = new Intl.DateTimeFormat(lang, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="max-h-[64vh] space-y-3 overflow-y-auto pr-1">
      {groups.map((g) => (
        <div key={dayKey(g.date)}>
          <button
            onClick={() => onSelectDay(g.date)}
            className="sticky top-0 z-[1] mb-1.5 flex w-full items-center gap-2 bg-white/95 py-1 text-left text-sm font-bold text-gray-800 backdrop-blur transition-colors hover:text-[#3B7BBE] dark:bg-[#24243e]/95 dark:text-gray-100"
          >
            <span className="capitalize">{dayFmt.format(g.date)}</span>
            <span className="rounded-md bg-[#3B7BBE]/10 px-2 py-0.5 text-xs font-bold text-[#3B7BBE]">
              {g.scans.length.toLocaleString(lang)}
            </span>
          </button>
          <div className="space-y-1.5">
            {g.scans.map((s) => (
              <ScanRow key={s.id} scan={s} hospitalColor={hospitalColor} showDate />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Some API rows carry the literal string "null" instead of a JS null, so a plain
// `||` chain would print "null". Treat those as empty (mirrors Live Activity).
function isBlank(v?: string | null): boolean {
  if (v == null) return true;
  const s = String(v).trim().toLowerCase();
  return s === "" || s === "null" || s === "undefined" || s === "n/a";
}

// Device label with the same fallback order as the Live Activity feed, plus the
// handset serial as a last resort: checkpoint-hospital display name → raw device
// account name → device account id → scanner serial.
function deviceLabel(scan: RecentScan): string {
  if (!isBlank(scan.display_device_name)) return String(scan.display_device_name);
  if (!isBlank(scan.guard_name)) return String(scan.guard_name);
  if (!isBlank(scan.guard_id)) {
    const cleaned = String(scan.guard_id).replace(/[_-]/g, " ").replace(/\s+/g, " ").trim();
    if (cleaned) return cleaned;
  }
  if (!isBlank(scan.scanner_serial)) return String(scan.scanner_serial);
  return "—";
}

// ── Single scan event row (shared by Day + List views) ───────────────────────
function ScanRow({
  scan,
  hospitalColor,
  showDate,
}: {
  scan: RecentScan;
  hospitalColor: (name: string | undefined) => string;
  showDate: boolean;
}) {
  const { t, lang } = useTranslation();
  const d = new Date(scan.timestamp);
  const violation = isViolation(scan);

  const timeStr = new Intl.DateTimeFormat(lang, {
    ...(showDate ? { month: "short", day: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(d);

  const checkpoint = scan.location_name || t("calendar.unknownCheckpoint", "Unknown checkpoint");
  // The officer who made the scan (signed on with their N number). This row used
  // to print `guard_name`, which is the handset's login ACCOUNT, not a person —
  // so the officer never appeared here even on fully attributed scans.
  const officerName = isBlank(scan.officer_name) ? null : String(scan.officer_name);
  const officerNumber = isBlank(scan.officer_n_number) ? null : String(scan.officer_n_number);
  const device = deviceLabel(scan);
  const hospital = scan.hospital_name || t("calendar.unknownHospital", "Unknown hospital");

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-white p-2.5 transition-colors hover:bg-gray-50 dark:bg-[#262626] dark:hover:bg-[#303030]",
        violation
          ? "border-[#EE3E2E]/40"
          : "border-gray-100 dark:border-[#383838]"
      )}
    >
      {/* Hospital color bar */}
      <span
        className="h-9 w-1.5 flex-shrink-0 rounded-full"
        style={{ backgroundColor: hospitalColor(scan.hospital_name) }}
      />

      {/* Time */}
      <div className="w-20 flex-shrink-0 text-xs font-bold tabular-nums text-gray-700 dark:text-gray-200">
        {timeStr}
      </div>

      {/* Checkpoint + hospital */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-gray-900 dark:text-gray-100">
          {checkpoint}
        </div>
        <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
          {hospital}
        </div>
      </div>

      {/* Officer + device */}
      <div className="hidden min-w-0 flex-1 sm:block">
        <div className="truncate text-xs font-semibold text-gray-700 dark:text-gray-300">
          <span className="text-gray-400 dark:text-gray-500">{t("live.colOfficer", "Officer")}: </span>
          {officerName ? (
            <>
              {scan.officer_id ? (
                <Link
                  to={`/supervisor/officers/${scan.officer_id}`}
                  className="text-[#3B7BBE] hover:underline"
                >
                  {officerName}
                </Link>
              ) : (
                officerName
              )}
              {officerNumber && (
                <span className="ml-1 font-mono font-medium text-gray-500 dark:text-gray-400">
                  ({officerNumber})
                </span>
              )}
            </>
          ) : (
            // No officer was signed on when this scan happened.
            <span className="font-medium text-gray-400 dark:text-gray-500">
              {t("live.unattributed", "Unattributed")}
            </span>
          )}
        </div>
        <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
          <span className="text-gray-400 dark:text-gray-500">{t("calendar.colDevice", "Device")}: </span>
          {device}
        </div>
      </div>

      {violation && (
        <span className="flex flex-shrink-0 items-center gap-1 rounded-md bg-[#EE3E2E]/10 px-2 py-1 text-[10px] font-bold uppercase text-[#EE3E2E]">
          <AlertTriangle className="h-3 w-3" />
          {t("calendar.violation", "Violation")}
        </span>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-[#33334d]">
        <CalendarDays className="h-7 w-7 text-gray-400" />
      </div>
      <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
        {t("calendar.noActivityRange", "No scan activity in this period.")}
      </p>
    </div>
  );
}
